import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { getRecentLinks } from '../../utils/keyboardRecentLinks';

const DESKTOP_PLATFORMS = ['tdesktop', 'macos', 'web', 'weba'];
const platform = window.Telegram?.WebApp?.platform || '';
export const isDesktopPlatform = DESKTOP_PLATFORMS.includes(platform);

const KeyboardContext = createContext(null);

export const useKeyboard = () => {
    const context = useContext(KeyboardContext);
    if (!context) {
        throw new Error('useKeyboard must be used within KeyboardProvider');
    }
    return context;
};

/**
 * Нативный сеттер для value — позволяет триггерить React onChange
 */
const nativeInputSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype, 'value'
).set;

/**
 * Устанавливает value через нативный сеттер + инвалидирует React _valueTracker,
 * чтобы React гарантированно увидел изменение и вызвал onChange.
 */
const setNativeValue = (el, value) => {
    // Запоминаем предыдущее значение для React tracker
    const prevValue = el.value;
    // Устанавливаем новое значение через нативный сеттер
    nativeInputSetter.call(el, value);
    // Инвалидируем React internal value tracker
    // (без этого React может не увидеть изменение и пропустить onChange)
    const tracker = el._valueTracker;
    if (tracker) {
        tracker.setValue(prevValue);
    }
    // Отправляем input event (React слушает именно его для onChange)
    el.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Проверяем нужно ли перехватывать этот input
 */
const shouldInterceptInput = (el) => {
    if (!el || el.tagName !== 'INPUT') return false;
    if (el.disabled) return false;
    // Пропускаем paste-инпут внутри клавиатуры (он для нативного paste)
    if (el.dataset.kbPasteInput) return false;
    // Если уже помечен нашим перехватчиком — OK
    if (el.dataset.kbIntercepted) return true;
    // Если readOnly поставлен пользователем (не нами) — пропускаем
    if (el.readOnly) return false;
    const skipTypes = ['hidden', 'checkbox', 'radio', 'file', 'submit', 'button', 'reset'];
    if (skipTypes.includes(el.type)) return false;
    return true;
};

/**
 * Определяем тип клавиатуры для инпута
 */
const getKeyboardTypeForInput = (el) => {
    const origType = el.dataset.kbOrigType || el.type;
    if (
        el.getAttribute('inputmode') === 'numeric' ||
        origType === 'number' ||
        origType === 'tel' ||
        (el.pattern && el.pattern.includes('[0-9]'))
    ) {
        return 'numeric';
    }
    return 'qwerty';
};

export const KeyboardProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [keyboardType, setKeyboardType] = useState('qwerty');
    const [inputValue, setInputValue] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [inputLabel, setInputLabel] = useState('');
    const [clipboardContent, setClipboardContent] = useState('');
    const [recentLinks, setRecentLinks] = useState([]);
    const [recentLinksLabel, setRecentLinksLabel] = useState('Recent links');
    
    const inputRef = useRef(null);
    const onChangeCallback = useRef(null);
    const onSubmitCallback = useRef(null);
    const onNavigateCallback = useRef(null);
    const liveUpdateRef = useRef(false);
    const activeInputElRef = useRef(null);
    const isOpenRef = useRef(false);
    // Refs для актуальных значений (чтобы paste handler видел текущее состояние)
    const inputValueRef = useRef('');
    const cursorPositionRef = useRef(0);

    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
    useEffect(() => { inputValueRef.current = inputValue; }, [inputValue]);
    useEffect(() => { cursorPositionRef.current = cursorPosition; }, [cursorPosition]);

    const loadRecentLinksForElement = useCallback((el) => {
        const key = el?.dataset?.kbRecentKey;
        setRecentLinks(key ? getRecentLinks(key) : []);
    }, []);

    const resolveActiveInputEl = useCallback(() => {
        const el = activeInputElRef.current;
        if (el?.isConnected) return el;

        // Inner React components sometimes remount the <input> while the keyboard
        // is open — reattach to the replacement node by recent-key / placeholder.
        const key = el?.dataset?.kbRecentKey;
        const placeholder = el?.placeholder || '';
        const next = key
            ? document.querySelector(`input[data-kb-recent-key="${CSS.escape(key)}"]`)
            : (placeholder
                ? document.querySelector(`input[data-kb-intercepted][placeholder="${CSS.escape(placeholder)}"]`)
                : null);
        if (next) {
            activeInputElRef.current = next;
            inputRef.current = next;
            return next;
        }
        return null;
    }, []);

    const applyInputValue = useCallback((newValue) => {
        setInputValue(newValue);
        setCursorPosition(newValue.length);
        inputValueRef.current = newValue;
        cursorPositionRef.current = newValue.length;

        if (liveUpdateRef.current && onChangeCallback.current) {
            onChangeCallback.current(newValue);
        }
        const el = resolveActiveInputEl();
        if (el) {
            setNativeValue(el, newValue);
        }
    }, [resolveActiveInputEl]);

    // ─── Попытка прочитать clipboard (не критично если не сработает) ───
    const tryReadClipboard = useCallback(async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const text = await navigator.clipboard.readText();
                if (text && text.trim() && text.length < 300) {
                    setClipboardContent(text.trim());
                    return text.trim();
                }
            }
        } catch (err) {
            // Ничего — paste будет работать через нативный event
        }
        setClipboardContent('');
        return '';
    }, []);

    // ─── Открыть кастомную клавиатуру для конкретного DOM инпута ───
    // Для перехваченных DOM-инпутов live-обновления идут через прямой
    // setNativeValue(el, ...) в handleKeyPress/insertText.
    // onChangeCallback НЕ используется (null), чтобы не дублировать setNativeValue.
    // onSubmitCallback вызывается только при закрытии — отправляет финальное значение
    // + change event (для onBlur хандлеров).
    const openForElement = useCallback((el) => {
        if (!el || isOpenRef.current) return;
        
        activeInputElRef.current = el;
        const kbType = getKeyboardTypeForInput(el);
        const val = el.value || '';
        
        setKeyboardType(kbType);
        setInputValue(val);
        setCursorPosition(val.length);
        setInputLabel(el.placeholder || '');
        setRecentLinksLabel('Recent links');
        loadRecentLinksForElement(el);
        
        // Обновляем refs немедленно (не ждём useEffect)
        inputValueRef.current = val;
        cursorPositionRef.current = val.length;
        
        // Не ставим onChangeCallback — live-обновления через setNativeValue в handleKeyPress
        onChangeCallback.current = null;
        // onSubmitCallback — только при закрытии клавиатуры
        onSubmitCallback.current = (finalVal) => {
            const liveEl = (activeInputElRef.current?.isConnected
                ? activeInputElRef.current
                : null)
                || (el.dataset?.kbRecentKey
                    ? document.querySelector(`input[data-kb-recent-key="${CSS.escape(el.dataset.kbRecentKey)}"]`)
                    : el.isConnected ? el : null);
            if (!liveEl) return;
            setNativeValue(liveEl, finalVal);
            liveEl.dispatchEvent(new Event('change', { bubbles: true }));
        };
        onNavigateCallback.current = null;
        inputRef.current = el;
        // liveUpdate = false, т.к. handleKeyPress сам обновляет DOM через activeInputElRef
        liveUpdateRef.current = false;
        isOpenRef.current = true;
        setIsOpen(true);
        
        // Пробуем прочитать буфер (не критично если не удастся)
        tryReadClipboard();
    }, [loadRecentLinksForElement, tryReadClipboard]);

    // ─── API: openKeyboard (для CustomInput и других прямых вызовов) ───
    const openKeyboard = useCallback(({ 
        type = 'qwerty', value = '', onChange, onSubmit, onNavigate,
        inputElement, liveUpdate = false, label = '',
        suggestions = [], suggestionsLabel,
    }) => {
        setKeyboardType(type);
        setInputValue(value);
        setCursorPosition(value.length);
        setInputLabel(label);
        // Empty string hides the section title (promoter); default only when omitted (tasks).
        setRecentLinksLabel(
            suggestionsLabel === undefined ? 'Recent links' : suggestionsLabel
        );

        const fromElement = inputElement?.dataset?.kbRecentKey
            ? getRecentLinks(inputElement.dataset.kbRecentKey)
            : [];
        const merged = [
            ...suggestions.map((s) => String(s || '').trim()).filter(Boolean),
            ...fromElement,
        ].filter((link, idx, arr) => arr.indexOf(link) === idx);
        setRecentLinks(merged);
        
        // Обновляем refs немедленно (не ждём useEffect после рендера)
        // Это предотвращает race condition, когда closeKeyboard читает старое значение
        inputValueRef.current = value;
        cursorPositionRef.current = value.length;
        
        onChangeCallback.current = onChange;
        onSubmitCallback.current = onSubmit;
        onNavigateCallback.current = onNavigate || null;
        inputRef.current = inputElement;
        activeInputElRef.current = inputElement || null;
        liveUpdateRef.current = liveUpdate;
        isOpenRef.current = true;
        setIsOpen(true);
        tryReadClipboard();
    }, [tryReadClipboard]);

    // ─── Закрыть клавиатуру ───
    const closeKeyboard = useCallback((options) => {
        const applied = options?.applied !== false;
        const finalValue = inputValueRef.current;
        const el = activeInputElRef.current;
        
        // Сначала помечаем как закрытую (чтобы глобальный interceptor не конфликтовал)
        isOpenRef.current = false;
        
        if (applied) {
            // onChangeCallback — для ручных openKeyboard (Redux dispatch и т.д.)
            if (onChangeCallback.current) {
                onChangeCallback.current(finalValue);
            }
            // onSubmitCallback — для всех случаев (финальное значение + change event)
            if (onSubmitCallback.current) {
                onSubmitCallback.current(finalValue);
            }
        }

        const liveEl = (el?.isConnected ? el : null)
            || (el?.dataset?.kbRecentKey
                ? document.querySelector(`input[data-kb-recent-key="${CSS.escape(el.dataset.kbRecentKey)}"]`)
                : null);
        
        if (liveEl) {
            if (applied) {
                setNativeValue(liveEl, finalValue);
            }
            // Небольшая задержка перед blur — чтобы React успел обработать
            // onChange и обновить state до того, как сработает onBlur
            setTimeout(() => {
                liveEl.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
            }, 50);
        }
        activeInputElRef.current = null;
        
        setIsOpen(false);
        setInputLabel('');
        setClipboardContent('');
        setRecentLinks([]);
        setRecentLinksLabel('Recent links');
        inputRef.current = null;
        onChangeCallback.current = null;
        onSubmitCallback.current = null;
        onNavigateCallback.current = null;
        liveUpdateRef.current = false;
    }, []);

    // ─── Вставить текст в текущую позицию ───
    const insertText = useCallback((text) => {
        if (!text) return;
        const val = inputValueRef.current;
        const pos = cursorPositionRef.current;
        const newValue = val.slice(0, pos) + text + val.slice(pos);
        const newCursor = pos + text.length;
        
        setInputValue(newValue);
        setCursorPosition(newCursor);
        setClipboardContent('');
        
        // Обновляем refs немедленно
        inputValueRef.current = newValue;
        cursorPositionRef.current = newCursor;
        
        if (liveUpdateRef.current && onChangeCallback.current) {
            onChangeCallback.current(newValue);
        }
        const el = resolveActiveInputEl();
        if (el) {
            setNativeValue(el, newValue);
        }
    }, [resolveActiveInputEl]);

    // ─── Paste: Clipboard API (вызов из user gesture — клик на кнопку) ───
    const pasteFromClipboard = useCallback(async () => {
        // Clipboard API — вызываем из user gesture (клик), что даёт доступ
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const text = await navigator.clipboard.readText();
                if (text && text.trim()) {
                    insertText(text.trim());
                    return;
                }
            }
        } catch (err) {
            console.log('Clipboard API not available, trying execCommand fallback');
        }
        
        // Fallback: execCommand('paste') через скрытый textarea
        // Не на всех устройствах работает, но попробуем
        try {
            const hidden = document.createElement('textarea');
            hidden.style.cssText = 'position:fixed;top:0;left:0;opacity:0.01;width:1px;height:1px;pointer-events:none;';
            hidden.setAttribute('autocomplete', 'off');
            hidden.setAttribute('autocorrect', 'off');
            document.body.appendChild(hidden);
            hidden.focus();
            
            const pasted = document.execCommand('paste');
            if (pasted && hidden.value && hidden.value.trim()) {
                insertText(hidden.value.trim());
            }
            
            document.body.removeChild(hidden);
        } catch (err) {
            // Тоже не сработало — ничего не делаем
            console.log('execCommand paste also failed');
        }
    }, [insertText]);

    // ─── Нажатие клавиши ───
    // Используем refs (inputValueRef, cursorPositionRef) вместо state,
    // чтобы при быстром вводе не было stale closure проблемы.
    const handleKeyPress = useCallback((key) => {
        const val = inputValueRef.current;
        const pos = cursorPositionRef.current;
        let newValue = val;
        let newCursor = pos;

        switch (key) {
            case 'BACKSPACE':
                if (pos > 0) {
                    newValue = val.slice(0, pos - 1) + val.slice(pos);
                    newCursor = pos - 1;
                }
                break;
            case 'SPACE':
                newValue = val.slice(0, pos) + ' ' + val.slice(pos);
                newCursor = pos + 1;
                break;
            case 'LEFT':
                if (onNavigateCallback.current) {
                    onNavigateCallback.current('up', inputValueRef.current);
                    return;
                }
                setCursorPosition(Math.max(0, pos - 1));
                return;
            case 'RIGHT':
                if (onNavigateCallback.current) {
                    onNavigateCallback.current('down', inputValueRef.current);
                    return;
                }
                setCursorPosition(Math.min(val.length, pos + 1));
                return;
            case 'HOME':
            case 'ROW_FIRST':
                if (onNavigateCallback.current) {
                    onNavigateCallback.current('first', inputValueRef.current);
                    return;
                }
                setCursorPosition(0);
                return;
            case 'ROW_UP':
                if (onNavigateCallback.current) {
                    onNavigateCallback.current('up', inputValueRef.current);
                }
                return;
            case 'ROW_DOWN':
                if (onNavigateCallback.current) {
                    onNavigateCallback.current('down', inputValueRef.current);
                }
                return;
            case 'ENTER':
            case 'DONE':
                closeKeyboard();
                return;
            case 'CLEAR':
                newValue = '';
                newCursor = 0;
                break;
            default:
                newValue = val.slice(0, pos) + key + val.slice(pos);
                newCursor = pos + 1;
        }

        setInputValue(newValue);
        setCursorPosition(newCursor);
        
        // Обновляем refs немедленно (до re-render)
        inputValueRef.current = newValue;
        cursorPositionRef.current = newCursor;
        
        if (liveUpdateRef.current && onChangeCallback.current) {
            onChangeCallback.current(newValue);
        }
        const el = resolveActiveInputEl();
        if (el) {
            setNativeValue(el, newValue);
        }
    }, [closeKeyboard, resolveActiveInputEl]);

    // ═══════════════════════════════════════════════════════════════
    // Глобальный перехват инпутов — ТОЛЬКО на мобильных платформах.
    // На десктопе пользователь работает с физической клавиатурой.
    // ═══════════════════════════════════════════════════════════════
    useEffect(() => {
        if (isDesktopPlatform) return;

        const processInput = (el) => {
            if (!shouldInterceptInput(el)) return;
            if (el.dataset.kbIntercepted) return;
            
            el.dataset.kbIntercepted = '1';
            
            // Сохраняем оригинальный type (для определения клавиатуры)
            if (el.type && el.type !== 'text') {
                el.dataset.kbOrigType = el.type;
            }
            // Меняем type="number" → type="text" (number не поддерживает inputMode)
            if (el.type === 'number') {
                el.type = 'text';
            }
            // inputMode="none" — подавляет нативную клавиатуру на мобильных,
            // но НЕ делает инпут readOnly → React onChange работает нормально
            el.setAttribute('inputmode', 'none');
            // Прячем нативный курсор
            el.style.caretColor = 'transparent';
        };
        
        // Обрабатываем все существующие инпуты
        document.querySelectorAll('input').forEach(processInput);
        
        // Наблюдаем за DOM — новые инпуты тоже перехватываем
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                for (const node of m.addedNodes) {
                    if (node.nodeType !== 1) continue;
                    if (node.tagName === 'INPUT') processInput(node);
                    if (node.querySelectorAll) {
                        node.querySelectorAll('input').forEach(processInput);
                    }
                }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        
        // Клик/тач по инпуту — открываем клавиатуру
        // НЕ вызываем stopPropagation, чтобы не блокировать onClick на родителях
        // (например, вставка обнаруженного токена из буфера)
        const handleClick = (e) => {
            const el = e.target;
            if (el.tagName !== 'INPUT' || !el.dataset.kbIntercepted) return;
            if (el.disabled) return;
            // Если readOnly стоит от React-компонента (не от нас) — пропускаем
            if (el.readOnly) return;
            if (isOpenRef.current) return;
            
            requestAnimationFrame(() => openForElement(el));
        };
        
        // Ловим focusin — блюрим инпут чтобы нативная клавиатура не выскакивала
        const handleFocusIn = (e) => {
            const el = e.target;
            if (el.tagName !== 'INPUT' || !el.dataset.kbIntercepted) return;
            if (el.disabled) return;
            if (el.readOnly) return;
            // Блюрим сразу, чтобы нативная клавиатура не успела открыться
            el.blur();
            if (!isOpenRef.current) {
                requestAnimationFrame(() => openForElement(el));
            }
        };
        
        document.addEventListener('click', handleClick, { capture: true });
        document.addEventListener('focusin', handleFocusIn, true);
        
        return () => {
            observer.disconnect();
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('focusin', handleFocusIn, true);
            // Восстанавливаем оригинальные атрибуты при размонтировании
            document.querySelectorAll('input[data-kb-intercepted]').forEach((el) => {
                if (el.dataset.kbOrigType) {
                    el.type = el.dataset.kbOrigType;
                    delete el.dataset.kbOrigType;
                }
                el.removeAttribute('inputmode');
                delete el.dataset.kbIntercepted;
                el.style.caretColor = '';
            });
        };
    }, [openForElement]);

    useEffect(() => {
        if (isDesktopPlatform) return;

        const handlePaste = (e) => {
            if (!isOpenRef.current) return;
            // Не перехватываем paste на внутреннем input клавиатуры
            // (CustomKeyboard.jsx обрабатывает его напрямую через onPaste)
            if (e.target?.dataset?.kbPasteInput) return;
            const text = (e.clipboardData || window.clipboardData)?.getData('text');
            if (text && text.trim()) {
                e.preventDefault();
                insertText(text.trim());
            }
        };
        
        document.addEventListener('paste', handlePaste, true);
        return () => document.removeEventListener('paste', handlePaste, true);
    }, [insertText]);

    const value = {
        isOpen,
        keyboardType,
        inputValue,
        cursorPosition,
        inputLabel,
        clipboardContent,
        recentLinks,
        recentLinksLabel,
        openKeyboard,
        closeKeyboard,
        handleKeyPress,
        pasteFromClipboard,
        tryReadClipboard,
        insertText,
        applyInputValue,
        setInputValue,
        setCursorPosition,
    };

    return (
        <KeyboardContext.Provider value={value}>
            {children}
        </KeyboardContext.Provider>
    );
};

export default KeyboardContext;
